import React, { useState, useEffect } from "react";
import openSocket from "../../services/socket-io";
import { useHistory } from "react-router-dom";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import Container from "@material-ui/core/Container";
import Select from "@material-ui/core/Select";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import { toast } from "react-toastify";

import api from "../../services/api";
import { i18n } from "../../translate/i18n.js";
import toastError from "../../errors/toastError";

const useStyles = makeStyles(theme => ({
	root: {
		display: "flex",
		alignItems: "center",
		padding: theme.spacing(8, 8, 3),
	},

	paper: {
		padding: theme.spacing(2),
		display: "flex",
		alignItems: "center",
		marginBottom: 12,

	},

	settingOption: {
		marginLeft: "auto",
	},
	margin: {
		margin: theme.spacing(1),
	},

}));

const Settings = () => {
	const classes = useStyles();
	const history = useHistory();

	const [settings, setSettings] = useState([]);

	// --- Estado do Corretor de @LID ---
	const [lidId, setLidId] = useState("");
	const [lidPhone, setLidPhone] = useState("");
	const [lidLoading, setLidLoading] = useState(false);
	const [lidLookupLoading, setLidLookupLoading] = useState(false);
	const [lidLookupMsg, setLidLookupMsg] = useState("");
	const [lidLookupSuccess, setLidLookupSuccess] = useState(false);

	// Consulta a API da Uazapi para tentar descobrir o número a partir do @lid
	const handleLookupLid = async () => {
		if (!lidId.trim()) {
			toast.warning("Informe o @LID para consultar.");
			return;
		}
		setLidLookupLoading(true);
		setLidLookupMsg("");
		setLidLookupSuccess(false);
		try {
			const { data } = await api.get("/lid-resolver/lookup", {
				params: { lid: lidId.trim() }
			});
			if (data.resolved && data.phoneNumber) {
				setLidPhone(data.phoneNumber);
				setLidLookupSuccess(true);
				setLidLookupMsg(`✅ Número encontrado: ${data.phoneNumber}${data.contactName ? ` (Contato: ${data.contactName})` : ""}. Clique em "Corrigir Contato" para salvar.`);
			} else {
				setLidLookupSuccess(false);
				setLidLookupMsg(`⚠️ ${data.message} Você pode digitar o número manualmente no campo abaixo.`);
			}
		} catch (err) {
			setLidLookupSuccess(false);
			setLidLookupMsg("❌ Erro ao consultar a API. Verifique se o WhatsApp está conectado.");
		} finally {
			setLidLookupLoading(false);
		}
	};

	const handleResolveLid = async () => {
		if (!lidId.trim() || !lidPhone.trim()) {
			toast.warning("Preencha o @LID e o número de telefone.");
			return;
		}
		setLidLoading(true);
		try {
			const { data } = await api.post("/lid-resolver", {
				lidId: lidId.trim(),
				phoneNumber: lidPhone.trim()
			});
			toast.success(data.message);
			setLidId("");
			setLidPhone("");
			setLidLookupMsg("");
			setLidLookupSuccess(false);
		} catch (err) {
			toastError(err);
		} finally {
			setLidLoading(false);
		}
	};
	// ------------------------------------

	useEffect(() => {
		const fetchSession = async () => {
			try {
				const { data } = await api.get("/settings");
				setSettings(data);
			} catch (err) {
				toastError(err);
			}
		};
		fetchSession();
	}, []);

	useEffect(() => {
		const socket = openSocket();

		socket.on("settings", data => {
			if (data.action === "update") {
				setSettings(prevState => {
					const aux = [...prevState];
					const settingIndex = aux.findIndex(s => s.key === data.setting.key);
					if (settingIndex !== -1) {
						aux[settingIndex].value = data.setting.value;
					} else {
						aux.push(data.setting);
					}
					return aux;
				});
			}
		});

		return () => {
			socket.disconnect();
		};
	}, []);

	const handleChangeSetting = async e => {
		const selectedValue = e.target.value;
		const settingKey = e.target.name;

		try {
			await api.put(`/settings/${settingKey}`, {
				value: selectedValue,
			});
			toast.success(i18n.t("settings.success"));
		} catch (err) {
			toastError(err);
		}
	};

	const getSettingValue = key => {
		const setting = settings.find(s => s.key === key);
		return setting ? setting.value : "";
	};

	return (
		<div className={classes.root}>
			<Container className={classes.container} maxWidth="sm">
				<Typography variant="body2" gutterBottom>
					{i18n.t("settings.title")}
				</Typography>
				<Paper className={classes.paper}>
					<Typography variant="body1">
						{i18n.t("settings.settings.userCreation.name")}
					</Typography>
					<Select
						margin="dense"
						variant="outlined"
						native
						id="userCreation-setting"
						name="userCreation"
						value={
							settings && settings.length > 0 && getSettingValue("userCreation")
						}
						className={classes.settingOption}
						onChange={handleChangeSetting}
					>
						<option value="enabled">
							{i18n.t("settings.settings.userCreation.options.enabled")}
						</option>
						<option value="disabled">
							{i18n.t("settings.settings.userCreation.options.disabled")}
						</option>
					</Select>

				</Paper>

				<Paper className={classes.paper}>
					<TextField
						id="api-token-setting"
						readonly
						label="Token Api"
						margin="dense"
						variant="outlined"
						fullWidth
						value={settings && settings.length > 0 && getSettingValue("userApiToken")}
					/>
				</Paper>

				<Paper className={classes.paper}>
					<TextField
						id="ticket-history-retention-setting"
						label={i18n.t("settings.settings.ticketHistoryRetention.name")}
						type="number"
						margin="dense"
						variant="outlined"
						name="ticketHistoryRetentionDays"
						value={
							settings && settings.length > 0 && getSettingValue("ticketHistoryRetentionDays")
						}
						onChange={handleChangeSetting}
						className={classes.settingOption}
						inputProps={{ min: 1 }}
					/>
					<Typography variant="caption" style={{ marginLeft: 8, color: "#666" }}>
						{i18n.t("settings.settings.ticketHistoryRetention.description")}
					</Typography>
				</Paper>

				<Paper className={classes.paper}>
					<Typography variant="body1">
						Ignorar Notificações de Grupos
					</Typography>
					<Select
						margin="dense"
						variant="outlined"
						native
						id="ignoreGroupMsg-setting"
						name="ignoreGroupMsg"
						value={
							settings && settings.length > 0 && getSettingValue("ignoreGroupMsg")
						}
						className={classes.settingOption}
						onChange={handleChangeSetting}
					>
						<option value="enabled">
							Ativado
						</option>
						<option value="disabled">
							Desativado
						</option>
					</Select>
				</Paper>

				<Paper className={classes.paper}>
					<Typography variant="body1">
						Ocultar Aba de Grupos
					</Typography>
					<Select
						margin="dense"
						variant="outlined"
						native
						id="hideGroupTab-setting"
						name="hideGroupTab"
						value={
							settings && settings.length > 0 && getSettingValue("hideGroupTab")
						}
						className={classes.settingOption}
						onChange={handleChangeSetting}
					>
						<option value="enabled">
							Ativado
						</option>
						<option value="disabled">
							Desativado
						</option>
					</Select>
				</Paper>

			<Paper className={classes.paper} style={{ flexDirection: "column", alignItems: "flex-start", padding: 16 }}>
					<div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
						<span style={{ fontSize: 22 }}>🔄</span>
						<Typography variant="body1" style={{ fontWeight: 700 }}>
							Distribuição Round Robin de Leads
						</Typography>
					</div>
					<Typography variant="body2" style={{ color: "#555", marginBottom: 8 }}>
						Distribui tickets pendentes automaticamente entre os agentes de uma fila de forma igualitária.
						O sistema seleciona sempre o agente com menos atendimentos abertos no dia.
						Em caso de empate, a escolha é feita por sorteio.
					</Typography>
					<Typography variant="caption" style={{ color: "#888", marginBottom: 12, display: "block" }}>
						<strong>Exemplo:</strong> 15 contatos + 10 vendedores = cada um recebe 1 lead,
						e 5 agentes são sorteados para receber o lead extra.
					</Typography>
					<Button
						variant="outlined"
						color="primary"
						size="small"
						onClick={() => history.push("/queues")}
						style={{ borderRadius: 8, textTransform: "none", fontWeight: 600 }}
					>
						⚙️ Configurar nas Filas
					</Button>
				</Paper>

			</Container>

			{/* ──────────── CORRETOR DE @LID ──────────── */}
			<Container className={classes.container} maxWidth="sm">
				<Paper
					className={classes.paper}
					style={{ flexDirection: "column", alignItems: "flex-start", padding: 16 }}
				>
					<div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
						<span style={{ fontSize: 20 }}>🔗</span>
						<Typography variant="body1" style={{ fontWeight: 700 }}>
							Corretor de @LID (WhatsApp Multi-Device)
						</Typography>
					</div>
					<Typography variant="body2" style={{ color: "#555", marginBottom: 12 }}>
						Quando o WhatsApp salva contatos com ID interno (<code>@lid</code>) em vez do número
						real, cole o ID abaixo e clique em <strong>"Consultar"</strong> — o sistema tentará
						descobrir o número automaticamente via API. Se não conseguir, você pode digitar o
						número manualmente.
					</Typography>

					<div style={{ display: "flex", flexDirection: "column", gap: 12, width: "100%" }}>

						{/* Campo @LID + botão Consultar na mesma linha */}
						<div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
							<TextField
								id="lid-resolver-lid-input"
								label="ID @LID recebido do cliente"
								variant="outlined"
								size="small"
								style={{ flex: 1 }}
								value={lidId}
								onChange={e => { setLidId(e.target.value); setLidLookupMsg(""); }}
								placeholder="176184356814897 ou 176184356814897@lid"
								disabled={lidLoading || lidLookupLoading}
							/>
							<Button
								id="lid-resolver-lookup-btn"
								variant="outlined"
								color="primary"
								size="small"
								onClick={handleLookupLid}
								disabled={lidLookupLoading || lidLoading}
								style={{ borderRadius: 8, textTransform: "none", fontWeight: 600, whiteSpace: "nowrap", height: 40 }}
								startIcon={lidLookupLoading ? <CircularProgress size={14} color="inherit" /> : null}
							>
								{lidLookupLoading ? "Consultando..." : "🔍 Consultar"}
							</Button>
						</div>

						{/* Mensagem de resultado da consulta */}
						{lidLookupMsg && (
							<Typography
								variant="caption"
								style={{
									color: lidLookupSuccess ? "#2e7d32" : "#b45309",
									background: lidLookupSuccess ? "#f0fdf4" : "#fffbeb",
									border: `1px solid ${lidLookupSuccess ? "#86efac" : "#fcd34d"}`,
									borderRadius: 6,
									padding: "8px 12px",
									display: "block"
								}}
							>
								{lidLookupMsg}
							</Typography>
						)}

						{/* Campo número (preenchido auto ou manual) */}
						<TextField
							id="lid-resolver-phone-input"
							label="Número real (DDI+DDD+número, ex: 5511999998888)"
							variant="outlined"
							size="small"
							fullWidth
							value={lidPhone}
							onChange={e => setLidPhone(e.target.value)}
							placeholder="5511999998888"
							disabled={lidLoading}
							helperText="Preenchido automaticamente pela consulta, ou digite manualmente."
						/>

						<Button
							id="lid-resolver-submit-btn"
							variant="contained"
							color="primary"
							size="small"
							onClick={handleResolveLid}
							disabled={lidLoading || !lidPhone.trim()}
							style={{ borderRadius: 8, textTransform: "none", fontWeight: 600, alignSelf: "flex-start" }}
							startIcon={lidLoading ? <CircularProgress size={16} color="inherit" /> : null}
						>
							{lidLoading ? "Corrigindo..." : "🔧 Corrigir Contato"}
						</Button>

					</div>
				</Paper>
			</Container>
		</div>
	);
};

export default Settings;
